// Full-document shell for toppers pages rendered entirely through React.
// reactivated's renderer injects the bundle CSS/JS + __PRELOADED_PROPS__, so we
// deliberately emit none of that here.
import React from "react";
import { FlashMessages } from "tsi-common-react/src/components/Message/index";
import { MetaTags } from "tsi-common-react/src/components/MetaTags/index";
import {
    initPromoCalloutMessages,
    useFocusFirstFormError,
    useLinkRewrites,
    useRichTextDecorators,
} from "tsi-common-react/src/utils/richTextDecorators";
import { watchForWistia } from "tsi-common-react/src/utils/wistia";
import { t } from "ttag";

import { Context, templates } from "@reactivated";

import { ExternalScript } from "@thelabnyc/thelabui/src/components/ExternalScript";
import { useSSRNonce } from "@thelabnyc/thelabui/src/hooks/useSSRNonce";
import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { Template as SessionKeepAlive } from "../../templates/SessionKeepAliveTemplate";
import { SiteFooter } from "../SiteFooter";
import { SiteHeader } from "../SiteHeader";

type IMessage =
    templates.UtilityPageTemplate["page_chrome"]["messages"][number];

// Server-rendered Django flash messages, rendered + dismissed by the shared
// FlashMessages component (which owns per-item dismissal). This wraps it with
// the toppers close icon and the no-JS fallback banner.
const MessagesCloseIcon = (): React.JSX.Element => (
    <svg
        className=""
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 25 25"
    >
        <title>Close</title>
        <path d="M12.245 15.5a7.49 7.49 0 0 0-5.855 2.813l-.78-.626a8.49 8.49 0 0 1 6.635-3.187 8.49 8.49 0 0 1 6.636 3.187l-.78.626a7.49 7.49 0 0 0-5.856-2.813m-2-6a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m5.5 1.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3" />
        <path
            fillRule="evenodd"
            d="M24.51 12.255c0 6.768-5.487 12.255-12.255 12.255S0 19.023 0 12.255 5.487 0 12.255 0 24.51 5.487 24.51 12.255m-1 0c0 6.216-5.04 11.255-11.255 11.255S1 18.47 1 12.255 6.04 1 12.255 1 23.51 6.04 23.51 12.255"
            clipRule="evenodd"
        />
    </svg>
);

const Messages = ({
    messages,
}: {
    messages: readonly IMessage[];
}): React.JSX.Element => (
    <>
        {messages.length > 0 && (
            <FlashMessages
                closeIcon={<MessagesCloseIcon />}
                messages={messages.map((message) => ({
                    tags: message.tags,
                    content: (
                        <p>
                            <span
                                className={`messages__item__category messages__item__category--${message.level_tag}`}
                            >
                                {message.category_label}
                            </span>{" "}
                            {message.is_html ? (
                                // Only messages explicitly mark_safe'd
                                // server-side render as HTML; plain
                                // messages are auto-escaped.
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: message.text,
                                    }}
                                />
                            ) : (
                                <span>{message.text}</span>
                            )}
                        </p>
                    ),
                }))}
            />
        )}
        <noscript
            dangerouslySetInnerHTML={{
                __html: `<aside class="messages l-full-width"><ul class="messages__list"><li class="messages__item error"><div class="messages__item__content l-capped-width"><p><span class="messages__item__category messages__item__category--error">${t`Error`}</span>${t`Please enable Javascript`}</p></div></li></ul></aside>`,
            }}
        />
    </>
);

// Mutable reactivated props (not Immutable<>): PageSkeleton renders the
// existing in-tree SiteHeader, which is typed with mutable reactivated props.
// The deeply-readonly StreamField body is handled in the body renderer.
//
// Per-page chrome (title, meta, canonical, JSON-LD, body_class, flash
// messages) is carried as the `page_chrome` field on each full-page
// @template. The global document shell (head/body globals, header, footer)
// comes from the `chrome` context processor (useContext below), not from
// these props. Typing `pageChrome` to the shared PageChrome (via
// UtilityPageTemplate) lets every full-document page template reuse
// PageSkeleton regardless of their other props.
type IProps = {
    pageChrome: templates.UtilityPageTemplate["page_chrome"];
    children?: React.ReactNode;
    // Header/footer slots. A node renders in place of the default
    // SiteHeader/SiteFooter; `false` omits the slot entirely; `undefined`
    // (default) renders the site chrome, so existing full-document pages are
    // unaffected.
    header?: React.ReactNode | false;
    footer?: React.ReactNode | false;
    // Messages slot. `false` omits the flash-messages block entirely
    // (including its noscript fallback), matching pages whose blanked
    // {% block header %} suppressed the _messages.html include (ReviewsPage
    // standalone); `undefined` (default) renders it. Pair with
    // build_page_chrome's include_messages=False so the flash queue is not
    // drained either.
    messages?: false;
};

const UnsupportedWarning = ({
    staticBase,
}: {
    staticBase: string;
}): React.JSX.Element => {
    const browsers = [
        {
            title: t`Get Google Chrome`,
            href: "http://www.google.com/chrome",
            img: "tsicommon_cms/img/chrome_32x32.png",
            label: t`Google Chrome`,
        },
        {
            title: t`Get Mozilla Firefox`,
            href: "http://www.firefox.com",
            img: "tsicommon_cms/img/firefox_32x32.png",
            label: t`Mozilla Firefox`,
        },
        {
            title: t`Get Apple Safari`,
            href: "http://www.apple.com/safari/download",
            img: "tsicommon_cms/img/safari_32x32.png",
            label: t`Apple Safari`,
        },
        {
            title: t`Get Microsoft Edge`,
            href: "https://www.microsoft.com/en-us/edge",
            img: "tsicommon_cms/img/edge_32x32.png",
            label: t`Microsoft Edge`,
        },
    ];
    return (
        <>
            <div className="unsupported-browser-container">
                <div className="unsupported-browser">
                    {/* eslint-disable-next-line react/no-unknown-property */}
                    <a
                        className="unsupported-browser__close-btn"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const container =
                                e.currentTarget.parentNode?.parentNode;
                            if (container instanceof HTMLElement) {
                                container.style.display = "none";
                            }
                        }}
                    >
                        {t`Close`}
                    </a>
                    <div className="unsupported-browser__title">
                        {t`Improve Your Shopping Experience`}
                    </div>
                    <p className="unsupported-browser__copy">
                        {t`You’re using an unsupported web browser.`}
                        <br />
                        {t`A list of the most popular browsers can be found below. Just click on an icon to visit the download page.`}
                    </p>
                    <ul className="unsupported-browser__list">
                        {browsers.map((b) => (
                            <li
                                className="unsupported-browser__list-item"
                                key={b.img}
                            >
                                <a
                                    title={b.title}
                                    href={b.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <img
                                        src={`${staticBase}${b.img}`}
                                        alt={b.title}
                                        title={b.title}
                                        loading="lazy"
                                        decoding="async"
                                    />
                                    <br />
                                    {b.label}
                                </a>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
            <div className="unsupported-mobileos-container">
                <div className="unsupported-mobileos">
                    <a
                        className="unsupported-mobileos__close-btn"
                        href="#"
                        onClick={(e) => {
                            e.preventDefault();
                            const container =
                                e.currentTarget.parentNode?.parentNode;
                            if (container instanceof HTMLElement) {
                                container.style.display = "none";
                            }
                        }}
                    >
                        {t`Close`}
                    </a>
                    <div className="unsupported-mobileos__title">
                        {t`Your Software is Out of Date`}
                    </div>
                    <p className="unsupported-mobileos__copy">
                        {t`Your phone or tablet is using software unsupported by our website. To improve your shopping experience, please update your device to the latest version of iOS or Android OS.`}
                    </p>
                </div>
            </div>
        </>
    );
};

export const PageSkeleton = (props: IProps): React.JSX.Element => {
    React.useEffect(() => watchForWistia(), []);
    const { pageChrome, children, header, footer, messages } = props;
    // Global chrome (header/nav, footer, head/body globals) comes from the
    // `chrome` context processor. PageSkeleton is always the full-document root
    // inside the reactivated Provider, so the context is populated in both SSR
    // and client hydration.
    const context = React.useContext(Context);
    const nonce = useSSRNonce();
    const chrome = context.chrome;
    // Post-hydration DOM decorators shared by every full-document page: fill CMS
    // rich-text promo tokens, apply the link-rewrite ruleset, and focus the
    // first form error.
    useRichTextDecorators([initPromoCalloutMessages]);
    useLinkRewrites();
    useFocusFirstFormError();
    // Class B: React owns <html>, so render it SSR-neutral and apply the
    // per-client mutations our legacy JS used to do pre-hydration AFTER commit.
    // index.tsx now guards the no-js removal to island pages and legacy.tsx
    // guards data-useragent to old (non-module) browsers; here we do both for
    // the full-document React render.
    React.useEffect(() => {
        const win = getWindow();
        const doc = getDocument();
        if (doc?.documentElement) {
            doc.documentElement.classList.remove("no-js");
            if (win?.navigator) {
                doc.documentElement.setAttribute(
                    "data-useragent",
                    win.navigator.userAgent,
                );
            }
        }
    }, []);
    return (
        <html className="no-js" lang={chrome.html_lang}>
            <head>
                {/*
                 * tempur.html uses an inline `html{visibility:hidden;opacity:0}`
                 * FOUC guard, relying on the bundle CSS (`html{visibility:visible}`)
                 * overriding it because the bundle <link> comes AFTER it in source
                 * order. In a full-document React render, React 19 hoists the bundle
                 * stylesheet to the TOP of <head> (before any inline <style>),
                 * inverting that order and leaving the page permanently hidden. The
                 * bundle <link> is render-blocking in <head> here, so there is no
                 * FOUC to guard against -- we intentionally omit the inline hide.
                 */}
                <title>{pageChrome.title}</title>
                <meta
                    httpEquiv="content-type"
                    content="text/html; charset=UTF-8"
                />
                <meta name="created" content={chrome.created} />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                />
                <meta
                    name="google-site-verification"
                    content={chrome.google_site_verification}
                />
                {pageChrome.search_description && (
                    <meta
                        name="description"
                        content={pageChrome.search_description}
                    />
                )}
                {pageChrome.search_keywords && (
                    <meta
                        name="keywords"
                        content={pageChrome.search_keywords}
                    />
                )}
                {pageChrome.robots_noindex && (
                    <meta name="robots" content="noindex" />
                )}
                <link rel="canonical" href={pageChrome.canonical_url} />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:site" content={chrome.twitter_site} />
                <meta name="twitter:title" content={pageChrome.seo_heading} />
                <meta
                    name="twitter:description"
                    content={pageChrome.search_description}
                />
                <meta name="twitter:url" content={pageChrome.current_url} />
                <meta property="og:title" content={pageChrome.seo_heading} />
                <meta property="og:type" content="website" />
                <meta property="og:url" content={pageChrome.current_url} />
                <meta
                    property="og:description"
                    content={pageChrome.search_description}
                />
                <meta property="og:site_name" content={chrome.og_site_name} />
                {/* Per-page social-share images ({% block extra_meta %} on the
                    pages that set them, e.g. PDP/PLP); no tag is rendered when
                    empty, so utility/form pages emit nothing. */}
                {pageChrome.twitter_image && (
                    <meta
                        name="twitter:image"
                        content={pageChrome.twitter_image}
                    />
                )}
                {pageChrome.og_image && (
                    <meta property="og:image" content={pageChrome.og_image} />
                )}
                <link
                    rel="shortcut icon"
                    type="image/x-icon"
                    href={chrome.favicon_url}
                />
                {/* Per-page stylesheet bundles ({% include_css %} additions in
                    {% block style %} on the pages that set them); rendered
                    after the reactivated bundle CSS so they win the cascade,
                    like the {{ block.super }} ordering. Empty on most
                    pages, which then emit no tag. */}
                {pageChrome.extra_stylesheets.map((href) => (
                    <link
                        key={href}
                        rel="stylesheet"
                        type="text/css"
                        href={href}
                    />
                ))}
            </head>
            <body
                id={chrome.body_id}
                className={pageChrome.body_class}
                data-appslug={chrome.app_slug}
                data-version={chrome.version}
                data-environment={chrome.environment}
                data-sentry-dsn={chrome.sentry_dsn}
                data-track-page-type={pageChrome.page_type}
                data-view-name={chrome.view_name}
            >
                {chrome.aws_waf_url && (
                    <ExternalScript src={chrome.aws_waf_url} defer={true} />
                )}
                {/*
                 * The consent stub, the third-party tags and the JSON-LD render
                 * at the TOP of <body>, not in <head>. React 19 hoists the
                 * <meta> and async <script src> tags up anyway, while an inline
                 * <script> in the React-owned <head> gets paired with whatever a
                 * third party injects there before hydration finishes -- shifting
                 * the whole head by one. OptanonWrapper only has to exist before
                 * the async OneTrust SDK calls it, GTM events queue in dataLayer,
                 * and JSON-LD is crawled anywhere.
                 */}
                <script
                    nonce={nonce}
                    type="text/javascript"
                    dangerouslySetInnerHTML={{
                        __html: "function OptanonWrapper() { }",
                    }}
                />
                <MetaTags
                    tags={context.meta_tags.meta_tags.value}
                    doNotTrack={chrome.do_not_track}
                />
                {pageChrome.json_ld.map((schema, i) => (
                    <script
                        key={i}
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{ __html: schema }}
                    />
                ))}
                <div className="layout">
                    {messages !== false && (
                        <Messages messages={pageChrome.messages} />
                    )}
                    {header === undefined ? (
                        <SiteHeader {...chrome.site_header} />
                    ) : (
                        header
                    )}
                    <main id="main-content">{children}</main>
                    {footer === undefined ? (
                        <SiteFooter footer={chrome.footer} />
                    ) : (
                        footer
                    )}
                    {/*
                     * UnsupportedWarning lives INSIDE .layout (not as a trailing
                     * body child) so that .layout is the LAST DOM-producing child
                     * of <body>. The toppers GTM container injects its Custom HTML
                     * tags (scroll-depth, pageCount, ImpactRadius) as direct
                     * <body> children immediately AFTER .layout. React 19 skips
                     * foreign body children that TRAIL all of our content, but
                     * throws a FATAL "tree will be regenerated" mismatch when a
                     * foreign node is interleaved BEFORE an expected positional
                     * node (verified: the injection raced hydration and collided
                     * with whatever we rendered after .layout -- first legacy.js,
                     * then this warning). Keeping every DOM-producing element
                     * inside .layout makes the GTM injection trailing -> skipped.
                     * (.layout is an unstyled block wrapper and the warning is
                     * display:none / position:fixed when shown, so nesting is
                     * layout-safe; SessionKeepAlive renders null and legacy.js is
                     * hoisted to <head>, so neither produces a trailing node.)
                     */}
                    <UnsupportedWarning staticBase={chrome.static_base} />
                </div>
                <SessionKeepAlive />
                {/*
                 * `async` (not `defer`) so React 19 hoists this <script src> to
                 * <head> as a managed resource instead of hydrating it
                 * positionally at body-end. The toppers GTM container fires an
                 * "All Pages" Custom HTML scroll-depth tag that GTM injects
                 * IMMEDIATELY BEFORE this script (verified: prev=div.layout,
                 * next=the GTM pageCount tag). As a positional body <script>
                 * vnode it raced React's hydration of this node -- when the GTM
                 * injection won, React paired its legacy.js vnode against the
                 * foreign <script> and threw a FATAL "tree will be regenerated"
                 * mismatch (intermittent, ~50% of loads). Hoisting removes the
                 * body-end script vnode entirely, so the foreign injection has
                 * nothing to collide with; React 19 skips foreign body/head
                 * <script> siblings cleanly. legacy.tsx is order-independent (it
                 * only sets data-useragent for old non-module browsers, which
                 * never boot React anyway), so async is semantically safe and
                 * old browsers still receive it from the SSR <head>.
                 */}
                <script
                    async={true}
                    src={`${chrome.static_base}dist2/legacy.js`}
                    nonce={nonce}
                    suppressHydrationWarning={true}
                />
            </body>
        </html>
    );
};
